<template>
  <div>
    <HeaderShadow></HeaderShadow>
    <Logo></Logo>

    <ErrorBox
      v-if="errorHeading || errorContent"
      :heading="errorHeading"
      :content="errorContent"
      :fixed="errorFixed"
      @closeError="
        errorHeading = '';
        errorContent = '';
      "
    ></ErrorBox>

    <div
      id="scrollBox"
      class="bg-white p-4 overflow-y-auto"
      :class="{ 'scroll-box': !isStatic }"
    >
      <div v-if="!isStatic" class="text-xl mb-6">
        {{ $t("pages.eulaPrivacyPolicy.pleaseRead") }}
      </div>
      <div v-if="isStatic" class="mb-6">
        <TextLink to="/" :name="$t('buttons.backToSignIn')"></TextLink>
      </div>
      <div v-if="eulaDoc" v-html="eulaDoc"></div>
      <div v-if="privacyDoc" v-html="privacyDoc"></div>
    </div>
    <div
      v-if="!isStatic"
      class="absolute bottom-0 left-0 w-full h-28 px-4 shadow-xl-top"
    >
      <div class="flex items-center justify-items-center h-full">
        <div class="mr-2 w-1/2">
          <ButtonSecondary
            type="button"
            :name="$t('buttons.cancel')"
            @click="logout()"
          ></ButtonSecondary>
        </div>
        <div class="ml-2 w-1/2">
          <Button
            type="button"
            :name="$t('buttons.accept')"
            :disabled="!activateButtons || loading"
            :loading="loading"
            @click="accept()"
          ></Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderShadow from "@/components/utils/HeaderShadow.vue";
import Logo from "@/components/utils/Logo.vue";
import Button from "@/components/utils/Button.vue";
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue";
import ErrorBox from "@/components/utils/ErrorBox.vue";
import { Auth } from "aws-amplify";
import router from "@/router";
import TextLink from "@/components/utils/TextLink.vue";
import { getLocalisedError } from "@/utils/apiUtils";
import { getDefaultRegion } from "@/utils/languages";
import {
  isValidRegion,
  getDefaultLanguage as getDefaultLanguageForRegion,
  findRegion,
} from "@/utils/regions";

@Options({
  components: {
    HeaderShadow,
    Logo,
    Button,
    ButtonSecondary,
    ErrorBox,
    TextLink,
  },
  data() {
    return {
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      element: HTMLElement,
      activateButtons: true,
      loading: false,
      eulaDoc: null,
      privacyDoc: null,
      user: null,
      isStatic: false,
      unwatch: undefined,
    };
  },
  created() {
    this.unwatch = this.$store.watch(
      (state: any, getters: any) => [getters.getLanguage, getters.getRegion],
      (
        [newLang, newRegion]: [string, string],
        [oldLang, oldRegion]: [string, string]
      ) => {
        let changed: "language" | "region" | null = null;
        if (newLang !== oldLang) changed = "language";
        else if (newRegion !== oldRegion) changed = "region";

        if (changed) {
          // console.log(
          //   changed === "language"
          //     ? `Language changed from ${oldLang} to ${newLang}`
          //     : `Region changed from ${oldRegion} to ${newRegion}`
          // );
          this.loadDocuments(changed);
        }
      }
    );
  },
  mounted() {
    //this.hasScrolled();

    this.loadDocuments();

    this.user = this.$store.getters.getUserObject;

    if (this.$route.query.static) {
      this.isStatic = true;
    } else {
      if (!this.user || this.user.attributes["custom:agreement"] === "1") {
        router.push({ name: "Login" });
      }
    }
  },
  beforeUnmount() {
    this.unwatch();
  },
  methods: {
    /*
    hasScrolled() {
      this.element = document.getElementById("scrollBox");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.element.addEventListener("scroll", (event: any) => {
        var element = event.target;
        if (
          element.offsetHeight + Math.ceil(element.scrollTop) >=
          element.scrollHeight
        ) {
          this.activateButtons = true;
        }
      });
    },
    */
    loadDocuments(changed: "language" | "region" | null) {
      let language =
        (this.$route.query.lang as string) || this.$store.getters.getLanguage;
      let region =
        (this.$route.query.region as string) || this.$store.getters.getRegion;

      // 1) If you just changed (or initially set) language,
      //    verify the current region still supports it
      if (changed === "language" || !changed) {
        const regionObj = findRegion(region);
        if (!regionObj || !regionObj.languages.includes(language)) {
          const defaultReg = getDefaultRegion(language);
          // console.log(
          //   `Region "${region}" not valid for "${language}", switching to default ${defaultReg}`
          // );
          region = defaultReg;
          this.$store.dispatch("changeRegion", region);
        }
      }

      // 2) If you just changed region,
      //    verify the current language is supported there
      if (changed === "region") {
        const regionObj = findRegion(region);
        if (!regionObj || !regionObj.languages.includes(language)) {
          const defaultLang = getDefaultLanguageForRegion(region);
          // console.log(
          //   `Language "${language}" not valid for region "${region}", switching to default ${defaultLang}`
          // );
          language = defaultLang;
          this.$store.dispatch("changeLanguage", language);
          this.$root.$i18n.locale = language;
        }
      }

      // console.log(
      //   `Loading EULA and Privacy Policy for region ${region} and language ${language}`
      // );
      this.getPrivacy(region, language);
      this.getEula(region, language);
    },

    async getPrivacy(region?: string, language?: string) {
      this.privacyDoc = await this.getDoc(
        "PrivacyPolicy.html",
        region,
        language
      );
    },
    async getEula(region?: string, language?: string) {
      this.eulaDoc = await this.getDoc("EULA.html", region, language);
    },
    async getDoc(
      name: string,
      region?: string,
      language?: string
    ): Promise<string | null> {
      const key = this.getDocumentKey(name, region, language);
      try {
        return await this.$store.dispatch("getDocs", key);
      } catch (error: any) {
        if (error.status === 404 && region && language) {
          // Try to get the default document for the region
          return await this.getDoc(name, region);
        } else if (error.status === 404 && (region || language)) {
          // Try to get the default document for any region
          return await this.getDoc(name);
        } else {
          // this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        }
      }
      return null;
    },
    getDocumentKey(name: string, region?: string, language?: string) {
      const splits = name.split(".", 2);
      let key = "";
      if (region) {
        key += `${region.toLowerCase()}_`;
      }
      key += splits[0];
      if (language) {
        key += `-${language.toLowerCase()}`;
      }
      if (splits.length > 0) {
        key += `.${splits[1]}`;
      }
      // Returned format region_filename-language.extension
      return key;
    },
    async accept() {
      this.loading = true;
      try {
        const updateUser = await Auth.updateUserAttributes(this.user, {
          ["custom:agreement"]: "1",
        });

        if (updateUser) {
          router.go(0);
        }
      } catch (error: any) {
        this.loading = false;
        this.errorFixed = true;
        this.errorHeading = "";
        this.errorContent = error.data?.message;
        console.log("ERROR", error);
      }
    },
    // async decline() {
    //   this.loading = true;
    //   try {
    //     const updateUser = await Auth.updateUserAttributes(this.user, {
    //       ["custom:agreement"]: "0",
    //     });

    //     if (updateUser) {
    //       router.go(0);
    //     }
    //   } catch (error: any) {
    //     this.loading = false;
    //     this.errorFixed = true;
    //     this.errorHeading = "";
    //     this.errorContent = error.data?.message;
    //     console.log("ERROR", error);
    //   }
    // },
    async logout() {
      try {
        await Auth.signOut();
        this.removeAccess();
        router.push({ name: "Login" });
      } catch (err) {
        console.log("ERROR", err);
      }
    },
    removeAccess() {
      this.$store.dispatch("setUserObject", null);
      this.$store.dispatch("setRoles", []);
    },
  },
})
export default class EulaPrivacyPolicy extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 357px);
}
</style>
